import { SortingState } from "@tanstack/react-table";
import { Constants } from "appConstants";
import { HeadingListing, Loading, StatusCard, Table } from "components";
import { Partner } from "models/partner/Partner";

import { IcoArrowRight } from "assets/icons";
import TableHelper from "helpers/TableHelper";
import { useQuerystringParams } from "hooks/querystringFilter";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { PartnerService } from "services";
import { useTenant } from "store/contextTenant";
import "../styles.scss";
import { PartnerFilters } from "./types";

export default function PartnerList() {
	const navigate = useNavigate();
	const { tenant } = useTenant();
	const [partners, setPartners] = useState<Partner[] | undefined>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [searchName, setSearchName] = useState<string>("");
	const [sorting, setSorting] = useState<SortingState>([]);
	const [filters, setFilters] = useState<any>({
		size: 10,
		page: 0,
		name: searchName
	});
	const { updateUrl } = useQuerystringParams<PartnerFilters>(
		setFilters,
		sorting,
		setSorting
	);

	const partnerService = new PartnerService();

	const partnerColumns: any[] = [
		{
			header: Constants.strings.pages.listing.partners.headers.partner,
			accessorKey: "name",
			meta: {
				columnClass: "name-column"
			},
			size: 25
		},
		{
			header: Constants.strings.pages.listing.partners.headers.cnpj,
			id: "establishments.cnpj",
			accessorKey: "establishments",
			size: 34,
			cell: (rowData: any) => {
				return rowData?.getValue()?.[0]?.cnpj
					? rowData.getValue()[0].cnpj
					: "-";
			}
		},
		{
			id: "establishments.address.city.name",
			header: Constants.strings.pages.listing.partners.headers.city,
			accessorKey: "establishments",
			size: 12,
			cell: (rowData: any) =>
				rowData?.getValue()?.[0]?.address?.city?.name
					? rowData.getValue()[0].address.city.name
					: "-"
		},
		{
			id: "establishments.address.city.state",
			header: Constants.strings.pages.listing.partners.headers.state,
			accessorKey: "establishments",
			size: 8,
			cell: (rowData: any) =>
				rowData?.getValue()?.[0]?.address?.city?.state
					? rowData.getValue()[0].address.city.state
					: "-"
		},
		{
			id: "status",
			header: Constants.strings.pages.listing.partners.headers.status,
			accessorKey: "status",
			size: 15.5,
			cell: (rowData: any) => <StatusCard status={rowData.getValue()} />
		},
		{
			header: "",
			accessorKey: "id",
			cell: (
				<div className="text-align--center align-items--center arrow-right">
					<IcoArrowRight />
				</div>
			),
			enableSorting: false,
			size: 5.5
		}
	];

	const parseParams = () => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const filtersParams = {
			...filters,
			name: filters.search
		};
		if ("search" in filtersParams) {
			delete filtersParams.search;
		}
		return {
			...sortParam,
			...filtersParams
		};
	};

	const onPageChange = (selectedPage: number, pageSize: number) => {
		const newFilters = {
			...filters,
			size: pageSize,
			page: selectedPage - 1
		};
		setFilters(newFilters);
	};

	useEffect(() => {
		const newFilters = {
			...filters,
			name: searchName
		};
		setFilters(newFilters);
	}, [searchName]);

	useEffect(() => {
		const sortParam = TableHelper.sortingStateToParam(sorting);
		const newFilters = {
			...filters,
			...sortParam
		};
		updateUrl(newFilters);
	}, [sorting]);

	useEffect(() => {
		setIsLoading(true);
		partnerService
			.getList(parseParams())
			.then((response: any) => {
				setPartners(Partner.createListFromResponse(response.content));
				setTotalPages(response.totalPages);
			})
			.catch(() => setPartners(undefined))
			.finally(() => setIsLoading(false));
	}, [filters, tenant]);

	return (
		<div className="partner-page">
			<HeadingListing
				title={Constants.strings.general.partners}
				setSearchValue={setSearchName}
				showAddButton={false}
			/>
			{!isLoading ? (
				<Table
					columns={partnerColumns}
					data={partners as Partner[]}
					sorting={sorting}
					setSorting={setSorting}
					numberOfInstancesOptions={[10, 50, 100]}
					currentPage={filters.page + 1}
					pageSize={filters.size}
					totalPages={totalPages}
					onPageChange={onPageChange}
					onClickRow={(id: string) => navigate(`/backoffice/partners/${id}`)}
				/>
			) : (
				<div className="partner-list-loading">
					<Loading type="primary" size={75} />
				</div>
			)}
		</div>
	);
}
